import { BasicButton, Link, PrimaryButton } from '../../../../widgets/buttons';
import { Dialog, DialogWindow, InputDialog, MessageDialog, QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { FormControl } from '../../../../widgets/formControl';
import { Icon } from '../../../../widgets/icon';
import { Input } from '../../../../widgets/input';
import { PageFooterButtons } from '../../../../widgets/headers';
import { PageHeaderContainer } from '../../../../widgets/pageHeader';
import { showErrorNotification, showNotification } from '../../../../widgets/notificationToaster';
import { SwitchGroup } from '../../../../widgets/switchGroup';
import { VerticalLayout } from '../../../../widgets/layouts';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { isEmpty } from 'lodash';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { graphql, useLazyLoadQuery } from 'react-relay/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { ContainerSmall } from 'src/components/widgets/layouts/containerVariants';
import { Section } from 'src/components/widgets/section';
import ValidationMessage from 'src/components/widgets/validation/validationMessage';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { BasicBetaButton } from 'src/shared/globals';
import { backofficeUrl, getUrlWithQueryParams, openFileDialog, stringFormat, T, toGlobalID, uploadFile, uuid, validateInputSecurity } from 'src/shared/helpers';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import JSONIcon from 'src/shared/icons/json-icon.svg';
import { dialogsPortal } from 'src/shared/portals';
import { useValidation, validationContext } from 'src/shared/validation';
import styled from 'styled-components';
import {
    addSideAction,
    editProductNameAction,
    editSideMandatoryAllVariants,
    editSideNameAction,
    editSideRotateAllVariants,
    findModelColorForModelSide,
    getAllModelSides,
    getProductIsResizable,
    mergeModelSideAction,
    removeAllColorsAction,
    removeModelOptionsAction,
    removeSideAction,
    setHeaderDescriptionCustomComponent,
    setProductImageAction,
    setProductProviderCodeAction,
    setProductResizable,
    swapSidesAction,
    uploadProductImageProgressAction,
    upsertModelOptionsAction,
    useProductConfigurationChangeStep,
    useProductConfigurationState
} from '../../state';
import { isDefaultColor } from '../../state/model/modelColor';
import { ModelOptions } from '../../state/model/modelOptions';
import { ModelSide } from '../../state/model/modelSide';
import { useModelSideManager } from '../../state/model/modelSideManager';
import { subscriptionQuery } from '../../state/queries/__generated__/subscriptionQuery.graphql';
import { subscription } from '../../state/queries/subscription';
import TemplateCatalog from '../../templateCatalog/templateCatalog';
import { printAreasQuery } from './__generated__/printAreasQuery.graphql';
import { PrintAreaEditorDialog } from './printAreaEditorDialog';
import PrintAreaItem from './printAreaItem';
import ProductImageUpload from './productImageUpload';
import ResizablePrintArea from './resizablePrintArea/resizablePrintArea';
import { SideActionDialog } from './sideActionDialog';

export const LargeMessageDialogWindow = styled(DialogWindow)`
    max-width: 700px;
`;

export const TemplatesDialogWindow = styled(DialogWindow)`
    max-width: 1600px;
    height: 100%;
`;

const ProductImageUploadCard = styled(Section)`
    h2 {
        font-weight: 700;
        font-size: 16px;
    }
    label {
        display: block;
        width: 100%;
        margin: 20px 0;
        font-size: 16px;
        line-height: 24px;
        max-width: 600px;
        margin: 18px auto 5px;
    }
`;

const ResizableSwitchContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
    align-self: flex-end;
    align-items: center;
`;

const ResizableSubtitle = styled.div`
    margin-top: 5px;
`;

const FormControlNameStyled = styled(FormControl)`
    margin-bottom: 0 !important;
`;

const JSONIconContainer = styled(Icon)`
    width: 40px;
    height: 40px;
    margin-top: 28px;

    svg {
        color: ${(props) => props.theme.app.primary_dark};
    }
`;

const NameWithIconContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 24px;
    align-items: flex-start;
    margin-bottom: 18px;
`;

const PrintAreas: FC<{}> = () => {
    const navigate = useNavigate();
    const { changeImage } = useModelSideManager();
    const { showDialog, closeDialog } = useDialogManager();
    const { state, dispatch } = useProductConfigurationState();
    const changeStep = useProductConfigurationChangeStep();
    const allModelSides = useMemo(() => getAllModelSides(state.product), [state.product]);
    const [previousSidesGuid, setPreviousSidesGuid] = useState<string[]>(allModelSides.map((x) => x.guid));
    const [fileImageToUpload, setFileImageToUpload] = useState<File | null>(null);
    const [showMappingDialog, setShowMappingDialog] = useState(false);

    const [currentEditingModelSide, setCurrentEditingModelSide] = useState<ModelSide | null>(null);
    const [currentEditingModelOptions, setCurrentEditingModelOptions] = useState<ModelOptions | null>(null);

    const [productName, setProductName] = useState('ProductName');
    const [providerCode, setProviderCode] = useState(state.product.providerCode);
    const [isResizable, setIsResizable] = useState(getProductIsResizable(state.product));
    const subscriptionResponse = useLazyLoadQuery<subscriptionQuery>(subscription, {});
    const routeParams = useParams();

    const { userInfo } = useAccountData();
    const isJsonProductsImportFeatureEnabled = useFeatureFlagWithConditions('jsonProductsImport', {
        userId: userInfo?.userID?.toString()
    });

    // Queries
    const query = useLazyLoadQuery<printAreasQuery>(
        graphql`
            query printAreasQuery {
                ecommerceSettings {
                    ecommerceName
                    hasProductsApi
                }
                salesChannels(status: ACTIVE) {
                    id
                    ecommerceSettings {
                        ecommerceName
                        remoteUrl
                    }
                    supportedProviders
                }
                seller {
                    marketplace
                }
                generalSettings {
                    isSideImageScalingEnabled
                }
            }
        `,
        {}
    );

    const isSideImageScalingEnabled = query.generalSettings.isSideImageScalingEnabled;

    // On side added, ask for image for the last side added
    useEffect(() => {
        if (allModelSides.some((x) => !previousSidesGuid.find((guid) => guid === x.guid)) && fileImageToUpload) {
            setPreviousSidesGuid(allModelSides.map((x) => x.guid));
            const side = state.sides[state.sides.length - 1];

            state.product.colors
                .flatMap((x) => x.sides)
                .filter((x) => x.originalSideGuid == side.guid)
                .forEach((modelSide) => {
                    changeImage(modelSide, fileImageToUpload, false, isSideImageScalingEnabled);
                });
        }
    }, [allModelSides]);

    // useEffect(() => {
    // 	if (showMappingDialog) {

    // 			setShowMappingDialog(false);

    // 			showDialog('mapping-variants',<ConnectModelDialog
    // 				forAddingNewSalesChannel={false}
    // 				forEditProduct={true}
    // 				buttonSaveText={T._("Save", "Admin")}
    // 				productID={state.product.id ?? ''}
    // 				additionalCodes={state.product.additionalCodes.map(c=> { return {code: c.code, salesChannelID: c.salesChannelId}} )}
    // 				productColors={state.product.colors}
    // 				salesChannels={[]}
    // 				selectedSalesChannelID={state.salesChannelID}
    // 				productSalesChannelID={state.salesChannelID}
    // 				modelColorMappings={state.product.colorMappings}
    // 				merchantProductOptionsConnected={[]}
    // 				onConfirm={(product, salesChannelID, productColorMappingsToSave)=> {

    // 					showNotification({
    // 						type: 'success',
    // 						message: T._('Template imported successfully!', 'Admin'),
    // 					});

    // 					closeDialog('mapping-variants');
    // 					window.location.href = url(`/products`);
    // 				}}
    // 		/>);
    // 		}
    // 	else
    // 		closeDialog('mapping-variants');

    // },[showMappingDialog]);

    // Form validation
    const _validationContext = useContext(validationContext);
    const productNameSecurityValidation = validateInputSecurity(productName);
    const validationProductName = useValidation([
        {
            key: 'validationProductName',
            validate: (value: string) => (isEmpty(value) || !productNameSecurityValidation.isValid ? true : false),
            message: productNameSecurityValidation.isValid
                ? T._('Required', 'Admin')
                : `${T._('The product name contains invalid characters', 'Admin')}: ${productNameSecurityValidation.invalidCharacters.join(', ')}`
        }
    ]);
    validationProductName.check(productName);

    // Use supportedVariants from the state instead of hook
    const isCurrentSalesChannelSupportVariants = state.supportedVariants;

    const handleSkipVariationsClick = () => {
        if (validatePrintAreas()) {
            // Remove all previous variants set for product
            if (!(state.product.colors.length == 1 && state.product.colors[0].colorID == 1)) dispatch(removeAllColorsAction());
            changeStep('preview');
        }
    };

    const handleGoToVariationsButtonClick = () => {
        if (validatePrintAreas()) {
            // Always go to variations when this specific button is clicked (only shown for channels that support variants)
            changeStep('variations');
        }
    };

    const validatePrintAreas = () => {
        let isValid = true;
        for (const modelSide of allModelSides) {
            if (modelSide.areas.length === 0 || modelSide.ppcm === 0) {
                const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);
                showNotification({
                    message: stringFormat(T._('First set the print area on the side {0} before proceeding to variations.', 'Admin'), side!.name),
                    type: 'danger',
                    dismiss: {
                        duration: 4000
                    }
                });
                isValid = false;
                break;
            }
        }

        return isValid;
    };

    const handleUploadProductImageClick = () => {
        if (!state.product.name || validateInputSecurity(state.product.name).isValid === false) {
            showNotification({
                message: T._('You need to set the product name', 'Admin'),
                type: 'danger',
                dismiss: {
                    duration: 4000
                }
            });
            return;
        }

        openFileDialog(async (files) => {
            if (files.length > 0) {
                try {
                    dispatch(uploadProductImageProgressAction(0));
                    const imageUrl = await uploadFile(files[0], isSideImageScalingEnabled);

                    dispatch(setProductImageAction(imageUrl));
                } catch {
                    showNotification({
                        message: T._('An error occurred during uploading image', 'Admin'),
                        type: 'danger',
                        dismiss: {
                            duration: 4000
                        }
                    });
                } finally {
                    dispatch(uploadProductImageProgressAction(null));
                }
            }
        }, '.jpg,.png,.jpeg,.webp,.heic,.avif');
    };

    const handleSideChangeName = (modelSide: ModelSide) => {
        const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);

        showDialog(
            'side-name',
            <InputDialog
                label={T._('Side name', 'Admin')}
                placeholder={T._('Type the name of the side', 'Composer')}
                defaultValue={side?.name ?? ''}
                onConfirm={(name) => {
                    if (name == '') {
                        showDialog('invalid-name', <MessageDialog message={T._('Please type a valid side name.', 'Admin')} />);
                        return;
                    }
                    closeDialog('side-name');
                    dispatch(editSideNameAction(modelSide, name));
                }}
            />
        );
    };

    const handleSideAddClick = () => {
        showDialog(
            'side-name',
            <InputDialog
                label={T._('Side name', 'Admin')}
                placeholder={T._('Type the name of the side', 'Composer')}
                buttonLabel={T._('Upload image', 'Admin')}
                onConfirm={(name) => {
                    if (name == '') {
                        showDialog('invalid-name', <MessageDialog message={T._('Please type a valid side name.', 'Admin')} />);
                        return;
                    }

                    const validationSecurity = validateInputSecurity(name);

                    if (validationSecurity.isValid == false) {
                        showDialog(
                            'invalid-name',
                            <MessageDialog
                                message={T._(
                                    `Please type a valid side name. Invalid characters detected. ${validationSecurity.invalidCharacters.join(', ')}`,
                                    'Admin'
                                )}
                            />
                        );
                        return;
                    }

                    closeDialog('side-name');

                    openFileDialog((files) => {
                        if (files.length > 0) {
                            setFileImageToUpload(files[0]);
                            dispatch(addSideAction(name));
                        }
                    }, '.jpg,.png,.jpeg,.webp,.heic,.avif');
                }}
            />
        );
    };

    const handleSideChangeImage = async (modelSide: ModelSide) => {
        openFileDialog((files) => {
            if (files.length > 0) {
                const fileImage = files[0];

                if (!query.generalSettings.isSideImageScalingEnabled) checkImageSize(fileImage);

                if (subscriptionResponse.subscription?.hasSidesPerVariant && state.product.colors.length > 1) {
                    showMultipleVariantsQuestion(
                        modelSide,
                        'IMAGE',
                        () => changeImage(modelSide, fileImage, false, isSideImageScalingEnabled),
                        () => {
                            state.product.colors.forEach((modelColor) => {
                                modelColor.sides
                                    .filter((x) => x.originalSideGuid == modelSide.originalSideGuid)
                                    .forEach((modelSide) => {
                                        changeImage(modelSide, fileImage, false, isSideImageScalingEnabled);
                                    });
                            });
                        }
                    );
                } else {
                    changeImage(modelSide, fileImage, false, isSideImageScalingEnabled);
                }
            }
        }, '.jpg,.png,.jpeg,.webp,.heic,.avif');
    };

    const checkImageSize = (fileImage: File) => {
        // create an image element with that selected file
        const img = new Image();
        img.src = window.URL.createObjectURL(fileImage);
        // as soon as the image has been loaded
        img.onload = function () {
            const width = img.naturalWidth;
            const height = img.naturalHeight;

            // unload it
            window.URL.revokeObjectURL(img.src);

            // check its dimensions
            if (width > 1024 || height > 1024) {
                showNotification({
                    message: T._('We suggest using images below 1024x1024 pixels to avoid slow loading time of the customizer', 'Admin'),
                    type: 'warning',
                    dismiss: {
                        duration: 10000
                    }
                });
            } else if (fileImage.size / 1024 / 1024 > 2) {
                showNotification({
                    message: T._('We suggest using images below 2MB to avoid slow loading time of the customizer', 'Admin'),
                    type: 'warning',
                    dismiss: {
                        duration: 10000
                    }
                });
            }
        };
    };

    const handleSideRemoveClick = (modelSide: ModelSide) => {
        const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);

        if (side) {
            showDialog(
                'delete-side',
                <QuestionDialog
                    message={stringFormat(T._(`Do you want remove the side '{0}'?`, 'Admin'), side.name)}
                    onYesClick={() => {
                        closeDialog('delete-side');
                        dispatch(removeSideAction(side));
                    }}
                />
            );
        }
    };

    const handleSideMoveUpClick = (modelSide: ModelSide) => {
        // Take the index from the list and not sortOrder because it's more reliable
        const index = allModelSides.indexOf(modelSide);

        if (index > 0) {
            const side1 = modelSide.originalSideGuid;
            const side2 = allModelSides[index - 1].originalSideGuid;
            dispatch(swapSidesAction(side1, side2));
        }
    };

    const handleSideMoveDownClick = (modelSide: ModelSide) => {
        // Take the index from the list and not sortOrder because it's more reliable
        const index = allModelSides.indexOf(modelSide);

        if (index < allModelSides.length - 1) {
            const side1 = modelSide.originalSideGuid;
            const side2 = allModelSides[index + 1].originalSideGuid;
            dispatch(swapSidesAction(side1, side2));
        }
    };

    const handleSideImageClick = (modelSide: ModelSide) => {
        showDialog(
            'side-actions',
            <SideActionDialog
                actions={['image', 'name', 'area']}
                onSelected={(action) => {
                    closeDialog('side-actions');
                    if (action === 'name') handleSideChangeName(modelSide);
                    else if (action === 'image') handleSideChangeImage(modelSide);
                    else if (action === 'area') handleSideChangeArea(modelSide);
                }}
            />
        );
    };

    const handleSideChangeMandatory = (modelSide: ModelSide, value: boolean) => {
        //dispatch(mergeModelSideAction(modelSide, { ...modelSide, mandatory: value }));
        dispatch(editSideMandatoryAllVariants(modelSide, value));
    };

    const handleSideChangeRotate = (modelSide: ModelSide, value: boolean) => {
        //dispatch(mergeModelSideAction(modelSide, { ...modelSide, canRotate: value }));
        dispatch(editSideRotateAllVariants(modelSide, value));
    };

    const handleSideChangeArea = async (modelSide: ModelSide) => {
        const modelColor = findModelColorForModelSide(state.product, modelSide)!;

        setCurrentEditingModelSide(modelSide);
        setCurrentEditingModelOptions(state.product.options.find((x) => x.colorGuid === modelColor.guid) ?? null);

        // Manually edit the url
        navigate(getUrlWithQueryParams(backofficeUrl('/product/print-area/print-area-editor')));
    };

    const handleApplyAreaToAll = (newModelSide: ModelSide, newModelOptions: ModelOptions | null) => {
        if (currentEditingModelSide) {
            state.product.colors.forEach((modelColor) => {
                const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == currentEditingModelSide.originalSideGuid);

                if (thisColorModelSide) {
                    dispatch(
                        mergeModelSideAction(thisColorModelSide, {
                            areas: newModelSide.areas.map((x) => ({
                                ...x,
                                layers: x.layers.map((y) => ({
                                    ...y,
                                    id: toGlobalID(uuid(), 'AreaLayer')
                                }))
                            })),
                            ppcm: newModelSide.ppcm,
                            canRotate: newModelSide.canRotate
                        })
                    );
                }

                if (newModelOptions) {
                    dispatch(upsertModelOptionsAction({ ...newModelOptions, colorGuid: modelColor.guid, optionID: null }));
                } else if (currentEditingModelOptions) {
                    dispatch(removeModelOptionsAction(modelColor));
                }
            });
        }
    };

    const showMultipleVariantsQuestion = (side: ModelSide, type: 'PRINT_AREA' | 'IMAGE', firstCallback: () => void, allCallback: () => void) => {
        const originalSide = state.sides.find((x) => x.guid === side.originalSideGuid)!;

        const message =
            type === 'PRINT_AREA'
                ? stringFormat(
                      T._('This product has multiple variants, do you want to change the {0} side print areas for all variants?', 'Admin'),
                      originalSide.name
                  )
                : stringFormat(
                      T._('This product has multiple variants, do you want to change the {0} side image for all variants?', 'Admin'),
                      originalSide.name
                  );

        showDialog(
            'apply-image',
            <MessageDialog
                windowDecorator={LargeMessageDialogWindow}
                message={message}
                buttons={[
                    {
                        label: T._('Cancel', 'Admin'),
                        outline: true,
                        onClick: () => closeDialog('apply-image')
                    },
                    {
                        label: T._('Change all', 'Admin'),
                        onClick: () => {
                            allCallback();
                            closeDialog('apply-image');
                        }
                    },
                    {
                        label: T._('Change only this one', 'Admin'),
                        onClick: () => {
                            firstCallback();
                            closeDialog('apply-image');
                        }
                    }
                ]}
            />
        );
    };

    const handleOnSelectTemplate = () => {
        showDialog(
            'ChooseTemplate',
            <Dialog title={T._('Select template', 'Admin')} windowDecorator={TemplatesDialogWindow} showCloseButton={true}>
                <TemplateCatalog
                    salesChannelOfflineID={query.salesChannels.find((s) => s.ecommerceSettings?.ecommerceName == 'NONE')?.id ?? ''}
                    targetSalesChannelID={state.salesChannelID}
                    targetEcommerce={state.ecommerceSalesChannel.length > 0 ? state.ecommerceSalesChannel[0] : 'NONE'}
                    targetProductSKU={state.product.code}
                    targetProductName={state.product.name}
                ></TemplateCatalog>
            </Dialog>
        );
    };

    const hangleChangeIsResizable = (value: boolean) => {
        dispatch(setProductResizable(value));

        setIsResizable(value);
    };

    //Detect changes in modelSide to show variants dialog on save
    const hasModelSideChanged = (currentSide: ModelSide, newSide: ModelSide): boolean => {
        if (!currentSide?.areas || !newSide?.areas) return false;
        if (currentSide.areas.length !== newSide.areas.length) return true;

        return currentSide.areas.some((area, i) => {
            const newArea = newSide.areas[i];

            return (
                area.boundsX !== newArea.boundsX ||
                area.boundsY !== newArea.boundsY ||
                area.boundsWidth !== newArea.boundsWidth ||
                area.boundsHeight !== newArea.boundsHeight
            );
        });
    };

    //Detect changes in modelOptions to show variants dialog on save
    const hasModelOptionsChanged = (currentOptions: ModelOptions | null, newOptions: ModelOptions | null): boolean => {
        // If both are null, nothing changed
        if (currentOptions === null && newOptions === null) return false;

        // If only one is null, it's a change
        if (currentOptions === null || newOptions === null) return true;

        const valuesAreDifferent = (a: any, b: any): boolean => {
            if (a === null && b === null) return false;

            if (typeof a === 'number' && typeof b === 'number') {
                return a.toFixed(2) !== b.toFixed(2);
            }

            return a !== b;
        };

        return (Object.keys(newOptions) as (keyof ModelOptions)[]).some((key) => {
            if (currentOptions.hasOwnProperty(key)) {
                return valuesAreDifferent(currentOptions[key], newOptions[key]);
            } else {
                return true; // key exists in newOptions but not in currentOptions
            }
        });
    };

    const handleClickJsonSetting = () => {
        showDialog(
            'JSON-settings-dialog',
            <InputDialog
                label={T._('Entity Reference Key', 'Admin')}
                defaultValue={providerCode ?? ''}
                onConfirm={(providerCode) => {
                    if (providerCode === '') {
                        showErrorNotification({
                            message: T._('Type a valid provider code', 'Admin')
                        });

                        return;
                    }

                    closeDialog('JSON-settings-dialog');

                    setProviderCode(providerCode);
                    dispatch(setProductProviderCodeAction(providerCode));
                }}
            />
        );
    };

    // Set the switch custom component
    useEffect(() => {
        const hasSalesChannel = !state.ecommerceSalesChannel.some((e) => e === 'NONE');

        if (!hasSalesChannel) {
            dispatch(
                setHeaderDescriptionCustomComponent(
                    <React.Fragment>
                        <ResizableSwitchContainer>
                            {isResizable && state.product.colors.length <= 1 && (
                                <PageHeaderContainer.TitleDescription>
                                    <PageHeaderContainer.Title>{T._('Resizable Product', 'Admin')}</PageHeaderContainer.Title>
                                    <ResizableSubtitle>
                                        <PageHeaderContainer.TitleDescription>
                                            {T._(
                                                'Offer products that extend customization beyond design, enabling customers to personalize measurements according to their preferences.',
                                                'Admin'
                                            )}{' '}
                                            <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/14234024887964-Resizable-products'>
                                                {T._(`Learn more`, 'Admin')}
                                            </Link>
                                        </PageHeaderContainer.TitleDescription>
                                    </ResizableSubtitle>
                                </PageHeaderContainer.TitleDescription>
                            )}
                            {state.product.id == null && (
                                <SwitchGroup label={T._('Resizable product', 'Admin')} checked={isResizable} onChange={hangleChangeIsResizable} />
                            )}
                        </ResizableSwitchContainer>
                    </React.Fragment>
                )
            );
        } else {
            dispatch(
                setHeaderDescriptionCustomComponent(
                    <React.Fragment>
                        <ResizableSwitchContainer>
                            <PageHeaderContainer.TitleDescription>
                                <PageHeaderContainer.Title>{state.product.name}</PageHeaderContainer.Title>
                                {isResizable && (
                                    <ResizableSubtitle>
                                        <PageHeaderContainer.TitleDescription>
                                            {T._(
                                                'Offer products that extend customization beyond design, enabling customers to personalize measurements according to their preferences.',
                                                'Admin'
                                            )}{' '}
                                            <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/14234024887964-Resizable-products'>
                                                {T._(`Learn more`, 'Admin')}
                                            </Link>
                                        </PageHeaderContainer.TitleDescription>
                                    </ResizableSubtitle>
                                )}
                            </PageHeaderContainer.TitleDescription>
                            {state.product.id != null && isJsonProductsImportFeatureEnabled && (
                                <ZTooltip
                                    position='left'
                                    title={T._(
                                        "Edit JSON setting - This setting is only needed for bulk import functionality to reference the entity in the import JSON. If you don't need the JSON bulk import functionality, you can ignore this parameter.",
                                        'Admin'
                                    )}
                                >
                                    <JSONIconContainer $hoverable onClick={handleClickJsonSetting}>
                                        <JSONIcon />
                                    </JSONIconContainer>
                                </ZTooltip>
                            )}
                            {state.product.id == null && (
                                <SwitchGroup label={T._('Resizable product', 'Admin')} checked={isResizable} onChange={hangleChangeIsResizable} />
                            )}
                        </ResizableSwitchContainer>
                    </React.Fragment>
                )
            );
        }

        return () => {
            dispatch(setHeaderDescriptionCustomComponent(null));
        };
    }, [isResizable, state.product.imageUrl, state.product.id, state.ecommerceSalesChannel]);

    return (
        <React.Fragment>
            <VerticalLayout>
                {!state.product.imageUrl && state.ecommerceSalesChannel.some((e) => e === 'NONE') ? (
                    <>
                        {(!isResizable || state.product.colors.length > 1) && (
                            <ProductImageUploadCard
                                description={
                                    <>
                                        <h2>{T._(`Name your product and upload an image`, 'Admin')}</h2>
                                        <label>
                                            {T._('Create your customizable product, name it and upload an image', 'Admin')}
                                            <br />
                                            {T._('We recommend to select the front side of the product in this step.', 'Admin')}
                                        </label>
                                        <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693-Create-a-customizable-product'>
                                            {T._(`Learn more`, 'Admin')}
                                        </Link>
                                    </>
                                }
                            >
                                <ContainerSmall>
                                    <FormControl label={T._('Product name', 'Admin')}>
                                        <Input
                                            type='text'
                                            value={state.product.name}
                                            onChange={(e) => {
                                                setProductName(e.target.value);
                                                dispatch(editProductNameAction(e.target.value));
                                            }}
                                            placeholder={T._('Enter product name', 'Admin')}
                                        />
                                        <ValidationMessage validationKey='validationProductName' />
                                    </FormControl>
                                    <ProductImageUpload onImageClick={handleUploadProductImageClick} />
                                </ContainerSmall>
                            </ProductImageUploadCard>
                        )}
                    </>
                ) : (
                    <React.Fragment>
                        {state.ecommerceSalesChannel.some((e) => e === 'NONE') && (
                            <NameWithIconContainer>
                                <FormControlNameStyled label={T._('Product name', 'Admin')}>
                                    <Input
                                        type='text'
                                        value={state.product.name}
                                        onChange={(e) => {
                                            setProductName(e.target.value);
                                            dispatch(editProductNameAction(e.target.value));
                                        }}
                                        placeholder={T._('Enter product name', 'Admin')}
                                    />
                                    <ValidationMessage validationKey='validationProductName' />
                                </FormControlNameStyled>
                                {isJsonProductsImportFeatureEnabled && (
                                    <ZTooltip
                                        position='left'
                                        title={T._(
                                            "Edit JSON setting - This setting is only needed for bulk import functionality to reference the entity in the import JSON. If you don't need the JSON bulk import functionality, you can ignore this parameter.",
                                            'Admin'
                                        )}
                                    >
                                        <JSONIconContainer $hoverable onClick={handleClickJsonSetting}>
                                            <JSONIcon />
                                        </JSONIconContainer>
                                    </ZTooltip>
                                )}
                            </NameWithIconContainer>
                        )}
                        {(!isResizable || state.product.colors.length > 1) && (
                            <Section
                                description={
                                    <>
                                        {T._(`This is your pre-configured customizable product.`, 'Admin')}
                                        <br />
                                        {T._(
                                            'You can choose a different image, change the print area, add more printable sides or move to the next step.',
                                            'Admin'
                                        )}
                                        <br />
                                        <br />
                                        <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693-Create-a-customizable-product'>
                                            {T._(`How to configure your customizable product.`, 'Admin')}
                                        </Link>
                                    </>
                                }
                            >
                                <ContainerSmall
                                    css={`
                                        margin-top: 40px;
                                    `}
                                >
                                    {allModelSides.map((modelSide, index) => {
                                        return (
                                            <PrintAreaItem
                                                key={modelSide.guid}
                                                showRemoveButton={state.sides.length > 1}
                                                modelColor={findModelColorForModelSide(state.product, modelSide)!}
                                                modelSide={modelSide}
                                                showMoveDownButton={index != state.sides.length - 1}
                                                showMoveUpButton={index != 0}
                                                showMandatoryCheckbox={allModelSides.length > 1}
                                                onImageClick={() => handleSideImageClick(modelSide)}
                                                onMoveDownClick={() => handleSideMoveDownClick(modelSide)}
                                                onMoveUpClick={() => handleSideMoveUpClick(modelSide)}
                                                onRemoveClick={() => handleSideRemoveClick(modelSide)}
                                                onNameChangeClick={() => handleSideChangeName(modelSide)}
                                                onAreaChangeClick={() => handleSideChangeArea(modelSide)}
                                                onImageChangeClick={() => handleSideChangeImage(modelSide)}
                                                onSetMandatorySide={(value) => handleSideChangeMandatory(modelSide, value)}
                                                onSetRotateSide={(value) => handleSideChangeRotate(modelSide, value)}
                                            />
                                        );
                                    })}

                                    {allModelSides.every((x) => x.areas.length > 0) && (
                                        <PrintAreaItem
                                            showRemoveButton={false}
                                            showMoveDownButton={false}
                                            showMoveUpButton={false}
                                            showMandatoryCheckbox={false}
                                            modelColor={null}
                                            modelSide={null}
                                            onImageClick={handleSideAddClick}
                                        />
                                    )}
                                </ContainerSmall>
                            </Section>
                        )}
                    </React.Fragment>
                )}

                {isResizable && state.product.colors.length <= 1 && (
                    <ResizablePrintArea
                        onSave={(modelOptions: ModelOptions[]) => {
                            const baseColor = state.product.colors.find((c) => c.colorID === 1)!;
                            dispatch(removeModelOptionsAction(baseColor));
                            modelOptions.forEach((modelOption) => {
                                dispatch(upsertModelOptionsAction(modelOption));
                            });

                            handleSkipVariationsClick();
                        }}
                    />
                )}
            </VerticalLayout>

            {(!isResizable || state.product.colors.length > 1) && (state.product.imageUrl || !state.ecommerceSalesChannel.some((e) => e === 'NONE')) && (
                <PageFooterButtons $fixed={true}>
                    {!state.product.id && query.seller?.marketplace === 'HP' && (
                        <BasicBetaButton onClick={handleOnSelectTemplate}>{T._('Set from template...', 'Admin')}</BasicBetaButton>
                    )}
                    {/* Footer for sales channels that support variants */}
                    {isCurrentSalesChannelSupportVariants && (
                        <>
                            <BasicButton
                                onClick={() => {
                                    if (
                                        state.product.name &&
                                        validateInputSecurity(state.product.name).isValid === true &&
                                        !state.sides.some((s) => !s.name || validateInputSecurity(s.name).isValid === false)
                                    )
                                        handleGoToVariationsButtonClick();
                                }}
                            >
                                {T._('Go to variations', 'Admin')}
                            </BasicButton>

                            {(state.product.id === null || (state.product.colors.length == 1 && isDefaultColor(state.product.colors[0]))) && (
                                <PrimaryButton
                                    onClick={() => {
                                        if (
                                            state.product.name &&
                                            validateInputSecurity(state.product.name).isValid === true &&
                                            !state.sides.some((s) => !s.name || validateInputSecurity(s.name).isValid === false)
                                        )
                                            handleSkipVariationsClick();
                                        else {
                                            if (!state.product.name || validateInputSecurity(state.product.name).isValid === false) {
                                                showNotification({
                                                    message: T._('You need to set the product name', 'Admin'),
                                                    type: 'warning',
                                                    dismiss: {
                                                        duration: 4000
                                                    }
                                                });
                                            } else {
                                                showNotification({
                                                    message: T._('You need to set the name for all product sides', 'Admin'),
                                                    type: 'warning',
                                                    dismiss: {
                                                        duration: 4000
                                                    }
                                                });
                                            }
                                        }
                                    }}
                                >
                                    {T._(`I don't offer variations, go to review`, 'Admin')}
                                </PrimaryButton>
                            )}
                        </>
                    )}
                    {/* Footer for sales channels that don't support variants */}
                    {!isCurrentSalesChannelSupportVariants && (
                        <PrimaryButton
                            onClick={() => {
                                if (
                                    state.product.name &&
                                    validateInputSecurity(state.product.name).isValid === true &&
                                    !state.sides.some((s) => !s.name || validateInputSecurity(s.name).isValid === false)
                                )
                                    handleSkipVariationsClick();
                                else {
                                    if (!state.product.name || validateInputSecurity(state.product.name).isValid === false) {
                                        showNotification({
                                            message: T._('You need to set the product name', 'Admin'),
                                            type: 'warning',
                                            dismiss: {
                                                duration: 4000
                                            }
                                        });
                                    } else {
                                        showNotification({
                                            message: T._('You need to set the name for all product sides', 'Admin'),
                                            type: 'warning',
                                            dismiss: {
                                                duration: 4000
                                            }
                                        });
                                    }
                                }
                            }}
                        >
                            {T._('Continue to preview', 'Admin')}
                        </PrimaryButton>
                    )}
                </PageFooterButtons>
            )}
            {routeParams.subpage === 'print-area-editor' &&
                currentEditingModelSide &&
                createPortal(
                    <PrintAreaEditorDialog
                        modelColor={findModelColorForModelSide(state.product, currentEditingModelSide)!}
                        modelSide={currentEditingModelSide}
                        modelOptions={currentEditingModelOptions}
                        disabledModelSides={state.disabledModelSides}
                        onClose={() => {
                            navigate(-1);
                        }}
                        onSave={(newModelSide, newModelOptions) => {
                            navigate(-1);

                            const currentColor = findModelColorForModelSide(state.product, currentEditingModelSide)!;

                            if (
                                subscriptionResponse.subscription?.hasSidesPerVariant &&
                                state.product.colors.length > 1 &&
                                hasModelSideChanged(currentEditingModelSide, newModelSide)
                            ) {
                                showMultipleVariantsQuestion(
                                    currentEditingModelSide,
                                    'PRINT_AREA',
                                    () => {
                                        dispatch(mergeModelSideAction(currentEditingModelSide, newModelSide));

                                        // Model options
                                        if (newModelOptions) {
                                            dispatch(upsertModelOptionsAction(newModelOptions));
                                        } else if (currentEditingModelOptions) {
                                            dispatch(removeModelOptionsAction(currentColor));
                                        }
                                    },
                                    () => handleApplyAreaToAll(newModelSide, newModelOptions)
                                );
                            } else {
                                handleApplyAreaToAll(newModelSide, newModelOptions);
                            }
                        }}
                    />,
                    dialogsPortal
                )}
        </React.Fragment>
    );
};

export default PrintAreas;
